import {
  deleteEntities,
  getAllEntitiesApply,
  getEntity,
  selectAllEntities,
  setEntities,
  updateEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities'
import { Injectable } from '@angular/core'
import { createStore } from '@ngneat/elf'
import { Project } from '../models/project'
import { ProjectDao } from '../services/dao/project.dao'
import { ProjectCommandParams } from '../../generated/efp-api'
import { PlanRepository } from './plan.repository'
import { Doka360ProjectSelectorService } from '../services/doka360-project-selector.service'
import { NO_STOCK_SELECTED } from '../pages/stock/stock.page'

export const projectStore = createStore({ name: 'project' }, withEntities<Project>())

@Injectable({
  providedIn: 'root',
})
export class ProjectRepository {
  private hasLoadedAllForScope = false

  public readonly projects$ = projectStore.pipe(selectAllEntities())

  constructor(
    private readonly projectDao: ProjectDao,
    private readonly planRepository: PlanRepository,
    private readonly doka360ProjectSelectorService: Doka360ProjectSelectorService
  ) {
    this.doka360ProjectSelectorService.currentDoka360ProjectUid$.subscribe(() => {
      this.hasLoadedAllForScope = false
    })
  }

  public async findAllByFavouriteId(favouriteId: number): Promise<Project[]> {
    const projects = await this.projectDao.findAllByFavouriteId(favouriteId)
    projectStore.update(upsertEntities(projects))
    return projects
  }

  public async findOne(projectId: number, includePlans: boolean): Promise<Project | undefined> {
    let project = projectStore.query(getEntity(projectId))

    if (!project) {
      project = await this.projectDao.findOne(projectId, includePlans)

      if (project) {
        this.doka360ProjectSelectorService.setCurrentDoka360ProjectUid(project.doka360ProjectUid)

        projectStore.update(upsertEntities(project))
      }
    }

    if (includePlans && project) {
      project.plans = await this.planRepository.findAllByProjectId(project.id)
    }

    return project
  }

  public async fetchAllStandaloneProjects(includePlans: boolean): Promise<Project[]> {
    const currentDoka360ProjectUid =
      this.doka360ProjectSelectorService.getCurrentDoka360ProjectUid()

    /* Use cache only for standalone user scope,
       because cache invalidation is not automatically trigger by other user/client changes
    */
    if (!currentDoka360ProjectUid && this.hasLoadedAllForScope) {
      const cachedProjects = projectStore.query(
        getAllEntitiesApply({
          filterEntity: (e) => e.doka360ProjectUid === undefined,
        })
      )

      if (cachedProjects && cachedProjects.length > 0) {
        return cachedProjects
      }
    }

    const projects = await this.projectDao.findAll(includePlans)
    projectStore.update(setEntities(projects))

    if (includePlans) {
      this.hasLoadedAllForScope = true
    }

    return projects
  }

  public async findAllByStockId(stockId: number): Promise<Project[]> {
    const projects = await this.projectDao.findAllByStockId(stockId)
    projectStore.update(upsertEntities(projects))
    return projects
  }

  public async create(params: ProjectCommandParams): Promise<number> {
    // TODO: optimistic creating or backend return project
    this.hasLoadedAllForScope = false
    return this.projectDao.create(params)
  }

  public async update(project: Project): Promise<void> {
    if (project.stockId === NO_STOCK_SELECTED) {
      project.stockId = null
    }

    await this.projectDao.update(project)
    projectStore.update(updateEntities(project.id, project))
  }

  public async openCloseProject(project: Project): Promise<void> {
    projectStore.update(updateEntities(project.id, project))
  }

  public async delete(project: Project): Promise<void> {
    await this.projectDao.delete(project)
    projectStore.update(deleteEntities(project.id))
  }
}
